import React, { useState, useEffect } from 'react'
import {
  ArrowTrendingUpIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { API_BASE_URL } from '@/services/api'

interface Transferencia {
  id: number
  descricao: string
  valor: number
  data_transferencia: string
  data_processamento?: string
  status: 'PENDENTE' | 'PROCESSADA' | 'CANCELADA'
  conta_origem: string
  conta_destino: string
  categoria?: string
  centro_custo?: string
  observacoes?: string
  numero_referencia?: string
  taxa?: number
  created_at: string
  updated_at: string
}

const TransferenciasPage: React.FC = () => {
  const [transferencias, setTransferencias] = useState<Transferencia[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('TODOS')
  const [showForm, setShowForm] = useState(false)
  const { token } = useAuthStore()

  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    conta_origem_id: '',
    conta_destino_id: '',
    data_transferencia: new Date().toISOString().split('T')[0],
    observacoes: '',
    taxa: ''
  })

  useEffect(() => {
    fetchTransferencias()
    fetchAccounts()
  }, [])

  const fetchTransferencias = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE_URL}/financeiro/transferencias`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Erro ao carregar transferências')
      }

      const data = await response.json()
      setTransferencias(data)
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAccounts(Array.isArray(data?.accounts) ? data.accounts : [])
      }
    } catch (err) {
      console.error('Erro ao buscar contas:', err)
    }
  }

  const handleProcessTransfer = async (transferId: number) => {
    try {
      console.log('Processando transferência:', transferId)

      const response = await fetch(`${API_BASE_URL}/financeiro/transferencias/${transferId}/processar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao processar transferência')
      }

      const result = await response.json()
      console.log('Transferência processada:', result)

      // Refresh the transfers list
      fetchTransferencias()

      // Show success message if needed
      setError(null)

    } catch (err: any) {
      console.error('Erro ao processar transferência:', err)
      setError(err.message || 'Erro ao processar transferência')
    }
  }

  const handleCreateTransfer = async (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.conta_origem_id === formData.conta_destino_id) {
      setError('Conta de origem deve ser diferente da conta de destino')
      return
    }

    try {
      const transferData = {
        descricao: formData.descricao,
        valor: parseFloat(formData.valor),
        conta_origem_id: parseInt(formData.conta_origem_id),
        conta_destino_id: parseInt(formData.conta_destino_id),
        data_transferencia: formData.data_transferencia,
        observacoes: formData.observacoes,
        taxa: formData.taxa ? parseFloat(formData.taxa) : 0
      }

      console.log('Criando transferência:', transferData)

      const response = await fetch(`${API_BASE_URL}/financeiro/transferencias`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Erro ao criar transferência')
      }

      // Reset form
      setFormData({
        descricao: '',
        valor: '',
        conta_origem_id: '',
        conta_destino_id: '',
        data_transferencia: new Date().toISOString().split('T')[0],
        observacoes: '',
        taxa: ''
      })

      setShowForm(false)
      fetchTransferencias() // Refresh list

    } catch (err: any) {
      setError(err.message || 'Erro ao criar transferência')
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSADA': return 'text-green-600 bg-green-100'
      case 'PENDENTE': return 'text-yellow-600 bg-yellow-100'
      case 'CANCELADA': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PROCESSADA': return <CheckCircleIcon className="w-4 h-4" />
      case 'PENDENTE': return <ClockIcon className="w-4 h-4" />
      case 'CANCELADA': return <ExclamationTriangleIcon className="w-4 h-4" />
      default: return <ClockIcon className="w-4 h-4" />
    }
  }

  const filteredTransferencias = transferencias.filter(transferencia => {
    const matchesSearch = transferencia.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transferencia.conta_origem.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transferencia.conta_destino.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transferencia.numero_referencia?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'TODOS' || transferencia.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const getTotals = () => {
    const filtered = filteredTransferencias
    const totalTaxas = filtered.reduce((sum, transferencia) => sum + (transferencia.taxa || 0), 0)

    return {
      total: filtered.reduce((sum, transferencia) => sum + transferencia.valor, 0),
      pendentes: filtered.filter(t => t.status === 'PENDENTE').reduce((sum, transferencia) => sum + transferencia.valor, 0),
      processadas: filtered.filter(t => t.status === 'PROCESSADA').reduce((sum, transferencia) => sum + transferencia.valor, 0),
      canceladas: filtered.filter(t => t.status === 'CANCELADA').reduce((sum, transferencia) => sum + transferencia.valor, 0),
      taxas: totalTaxas
    }
  }

  const totals = getTotals()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <ArrowTrendingUpIcon className="w-8 h-8 mr-3" />
              Transferências
            </h1>
            <p className="text-blue-100">
              Gerencie transferências entre contas e acompanhe movimentações
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 flex items-center space-x-2 font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Nova Transferência</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Transferido</h3>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.total)}</p>
            </div>
            <ArrowTrendingUpIcon className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Pendentes</h3>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(totals.pendentes)}</p>
            </div>
            <ClockIcon className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Processadas</h3>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.processadas)}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Canceladas</h3>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.canceladas)}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Total Taxas</h3>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.taxas)}</p>
            </div>
            <ArrowTrendingUpIcon className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por descrição, contas ou referência..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="TODOS">Todos os Status</option>
                <option value="PENDENTE">Pendentes</option>
                <option value="PROCESSADA">Processadas</option>
                <option value="CANCELADA">Canceladas</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referência
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransferencias.map((transferencia) => (
                <tr key={transferencia.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{transferencia.descricao}</div>
                      {transferencia.categoria && (
                        <div className="text-sm text-gray-500">{transferencia.categoria}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="text-sm text-gray-900">{transferencia.conta_origem}</div>
                      <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                      <div className="text-sm text-gray-900">{transferencia.conta_destino}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(transferencia.valor)}</div>
                      {transferencia.taxa && transferencia.taxa > 0 && (
                        <div className="text-sm text-gray-500">Taxa: {formatCurrency(transferencia.taxa)}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(transferencia.data_transferencia)}</div>
                    {transferencia.data_processamento && (
                      <div className="text-sm text-gray-500">Processado: {formatDate(transferencia.data_processamento)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transferencia.status)}`}>
                      {getStatusIcon(transferencia.status)}
                      <span className="ml-1">{transferencia.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{transferencia.numero_referencia || '-'}</div>
                    {transferencia.centro_custo && (
                      <div className="text-sm text-gray-500">{transferencia.centro_custo}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Visualizar"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="text-gray-600 hover:text-gray-900"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      {transferencia.status === 'PENDENTE' && (
                        <button
                          onClick={() => handleProcessTransfer(transferencia.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Processar Transferência"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        className="text-red-600 hover:text-red-900"
                        title="Cancelar/Excluir"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTransferencias.length === 0 && (
          <div className="text-center py-8">
            <ArrowTrendingUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma transferência encontrada</h3>
            <p className="text-gray-500">
              {transferencias.length === 0
                ? 'Adicione sua primeira transferência para começar'
                : 'Tente ajustar os filtros para encontrar o que procura'
              }
            </p>
          </div>
        )}
      </div>

      {/* Transfer Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-4">Nova Transferência</h3>

            <form className="space-y-4" onSubmit={handleCreateTransfer}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Transferência para conta poupança"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conta de Origem
                  </label>
                  <select
                    value={formData.conta_origem_id}
                    onChange={(e) => setFormData({...formData, conta_origem_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecione a conta origem</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name} - {formatCurrency(account.balance || 0)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conta de Destino
                  </label>
                  <select
                    value={formData.conta_destino_id}
                    onChange={(e) => setFormData({...formData, conta_destino_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecione a conta destino</option>
                    {accounts.map((account) => (
                      <option
                        key={account.id}
                        value={account.id}
                        disabled={account.id.toString() === formData.conta_origem_id}
                      >
                        {account.name} - {formatCurrency(account.balance || 0)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor da Transferência
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({...formData, valor: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taxa (opcional)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.taxa}
                    onChange={(e) => setFormData({...formData, taxa: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data da Transferência
                </label>
                <input
                  type="date"
                  value={formData.data_transferencia}
                  onChange={(e) => setFormData({...formData, data_transferencia: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Observações sobre a transferência..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Criar Transferência
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false)
                    setError(null)
                  }}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TransferenciasPage